import { useCallback, useEffect, useMemo, useState } from 'react';

export const OWN_GROUP_METRICS = Object.freeze([
    { id: 'beta', label: 'Beta', description: 'Own-group beta' },
    { id: 'selloff', label: 'Selloff capture', description: 'Selloff capture' },
    { id: 'r_squared', label: 'R²', description: 'Explained variation (R²)' },
    { id: 'paired', label: 'Paired observations', description: 'Paired observations' },
]);
const PREFIX = 'watchlist-table-own-group-statistics.v1';
const EMPTY = Object.freeze({});
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const hasSelectedMetric = (value) => OWN_GROUP_METRICS.some(({ id }) => value?.[id] === true);

export function readOwnGroupPreferences(owner) {
    if (!owner || typeof window === 'undefined') return EMPTY;
    try {
        const raw = window.localStorage.getItem(`${PREFIX}:${owner}`);
        if (!raw || raw.length > 32_000) return EMPTY;
        const parsed = JSON.parse(raw);
        return Object.fromEntries(Object.entries(parsed || {}).filter(([id, value]) => (
            uuid.test(id) && hasSelectedMetric(value)
        )).slice(0, 200).map(([id, value]) => [id, Object.fromEntries(
            OWN_GROUP_METRICS.map(({ id: metric }) => [metric, value[metric] === true]),
        )]));
    } catch { return EMPTY; }
}

export default function useWatchlistOwnGroupPreferences(owner, definitions) {
    const [stored, setStored] = useState(() => ({ owner, value: readOwnGroupPreferences(owner) }));
    const current = stored.owner === owner ? stored.value : readOwnGroupPreferences(owner);
    const preferences = useMemo(() => Object.fromEntries((definitions || [])
        .filter((item) => hasSelectedMetric(current[item.attribute_definition_id]))
        .map((item) => [item.attribute_definition_id, current[item.attribute_definition_id]])), [current, definitions]);
    useEffect(() => {
        if (stored.owner !== owner) setStored({ owner, value: readOwnGroupPreferences(owner) });
    }, [owner, stored.owner]);
    const changePreference = useCallback((id, value) => {
        if (!owner || !definitions.some((definition) => definition.attribute_definition_id === id)) return;
        setStored((latest) => {
            const current = latest.owner === owner ? latest.value : readOwnGroupPreferences(owner);
            const next = { ...current };
            if (hasSelectedMetric(value)) next[id] = Object.fromEntries(OWN_GROUP_METRICS.map(
                ({ id: metric }) => [metric, value[metric] === true],
            ));
            else delete next[id];
            try { window.localStorage.setItem(`${PREFIX}:${owner}`, JSON.stringify(next)); }
            catch { /* Keep the current session usable when browser storage is unavailable. */ }
            return { owner, value: next };
        });
    }, [owner, definitions]);
    return { preferences, changePreference };
}
